import React from 'react';
import { MOBILE_BATTLE_DOCK_LIFT_PX } from './mobileBattleLayoutOffsets';

/**
 * Mobile Player Preparation Bonus and Diplomatic Accord Component
 * Positioned on top of the player drone zone
 */
export const MobilePlayerBonuses = ({
  preparationBonus,
  preparationBonusUsed,
  preparationBonusPendingRef,
  playerGoesFirst,
  diplomaticAccord,
  yourTurn,
  activatePreparationBonus,
  isAutobattler = false,
  isAutobattlerSpectator = false,
  gameState = null,
  blackMarketUsesThisBattle = 0,
  planAheadUsesThisBattle = 0,
  t
}) => {
  const hasPrepBonus = (preparationBonus === 'plan_ahead' || preparationBonus === 'black_market' || preparationBonus === 'team_leader') && playerGoesFirst === false;
  const blackMarketMaxUses = (() => {
    const n = Number(gameState?.exploreUnknownBlackMarketUsesPerBattle);
    return Number.isFinite(n) && n > 0 ? n : 1;
  })();
  const planAheadMaxUses = (() => {
    const n = Number(gameState?.exploreUnknownPlanAheadUsesPerBattle);
    return Number.isFinite(n) && n > 0 ? n : 1;
  })();
  const blackMarketChargesRemaining = Math.max(0, blackMarketMaxUses - (Number(blackMarketUsesThisBattle) || 0));
  const planAheadChargesRemaining = Math.max(0, planAheadMaxUses - (Number(planAheadUsesThisBattle) || 0));
  const prepExhausted =
    preparationBonus === 'black_market'
      ? blackMarketChargesRemaining <= 0
      : preparationBonus === 'plan_ahead'
        ? planAheadChargesRemaining <= 0
        : preparationBonusUsed;
  const prepDisabled = prepExhausted || preparationBonusPendingRef.current || !yourTurn;
  const diplomaticAccords = Array.isArray(diplomaticAccord)
    ? diplomaticAccord.filter(Boolean)
    : (diplomaticAccord ? [diplomaticAccord] : []);
  const diplomaticAccordImageForKey = (accord) =>
    accord === 'strategic_coordination'
      ? 'strategic_coordination.jpg'
      : accord === 'public_relations'
        ? 'public_relations.jpg'
        : accord === 'emergency_protocol'
          ? 'emergency_protocol.jpg'
          : accord === 'command_directive'
            ? 'command_directive.jpg'
            : null;
  const diplomaticAccordTooltipForKey = (accord) =>
    accord === 'strategic_coordination'
      ? t('profile.diplomaticAccords.strategicCoordination')
      : accord === 'public_relations'
        ? t('profile.diplomaticAccords.publicRelations')
        : accord === 'emergency_protocol'
          ? t('profile.diplomaticAccords.emergencyProtocol')
          : t('profile.diplomaticAccords.commandDirective');

  if (!hasPrepBonus && diplomaticAccords.length === 0) return null;
  
  return (
    <div
      className="player-bonuses-mobile"
      style={{
        position: "absolute",
        bottom: `${40 + MOBILE_BATTLE_DOCK_LIFT_PX}px`,
        right: "75px",
        zIndex: 1002,
        display: "flex",
        gap: "2px",
        flexDirection: "row",
        transform: "scale(0.35)",
        transformOrigin: "bottom right"
      }}
    >
      {/* Player Preparation Bonus */}
      {hasPrepBonus && (
        <div 
          style={{ 
            width: "84px",
            height: "84px",
            pointerEvents: prepDisabled ? 'none' : 'auto'
          }}
        >
          <button
            className={`preparation-bonus-button ${prepDisabled ? 'disabled' : ''}`}
            onClick={(e) => {
              const ok = activatePreparationBonus(e);
              if (!ok) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            disabled={isAutobattler || isAutobattlerSpectator || prepDisabled}
            data-tooltip={
              prepExhausted
                ? t('battleUI.preparationBonusUsed') || 'Preparation bonus already used'
                : !yourTurn
                  ? t('battleUI.notYourTurn') || 'Not your turn'
                  : preparationBonus === 'plan_ahead'
                    ? planAheadMaxUses >= 2
                      ? t('profile.preparationBonus.planAheadTwoCharges', {
                          remaining: planAheadChargesRemaining,
                          total: planAheadMaxUses,
                        })
                      : t('profile.preparationBonus.planAhead')
                    : preparationBonus === 'black_market'
                      ? blackMarketMaxUses >= 2
                        ? t('profile.preparationBonus.blackMarketDossierTwoCharges', {
                            remaining: blackMarketChargesRemaining,
                            total: blackMarketMaxUses,
                          })
                        : t('profile.preparationBonus.blackMarketDossier')
                      : t('profile.preparationBonus.teamLeaderMeeting')
            }
            style={{
              width: "100%",
              height: "100%",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              margin: 0,
              ...(prepDisabled ? {
                filter: 'grayscale(100%)',
                cursor: 'not-allowed',
                pointerEvents: 'none',
                background: 'rgba(30, 30, 30, 0.7)'
              } : {
                cursor: 'pointer',
                background: 'rgba(50, 50, 50, 0.5)',
                filter: 'none'
              })
            }}
          >
            <img 
              src={`/images/${preparationBonus === 'plan_ahead' ? 'plan_ahead.jpg' : 
                            preparationBonus === 'black_market' ? 'black_market_dossier.jpg' : 
                            'team_leader_briefing.jpg'}`}
              alt={preparationBonus}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none"
              }}
            />
          </button>
        </div>
      )}
      
      {/* Player Diplomatic Accord(s) */}
      {diplomaticAccords.map((accord) => {
        const image = diplomaticAccordImageForKey(accord);
        if (!image) return null;
        return (
          <div
            key={accord}
            style={{
              width: "84px",
              height: "84px"
            }}
            data-tooltip={diplomaticAccordTooltipForKey(accord)}
            className="tooltip-multiline"
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                border: "2px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                margin: 0,
                background: 'rgba(50, 50, 50, 0.5)'
              }}
            >
              <img
                src={`/images/${image}`}
                alt={accord}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  pointerEvents: "none"
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MobilePlayerBonuses;

